import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Upload,
  Button,
  message,
  Progress,
  Typography,
  Space,
  Alert,
  Table,
  Tag,
  Modal,
  Divider,
  Row,
  Col,
  Statistic
} from 'antd';
import {
  UploadOutlined,
  FileExcelOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd/es/upload/interface';
import apiClient from '../../services/apiClient';
// import { packPriceImportService, PackPriceImport, ImportResult as ServiceImportResult } from '../../services/packPriceImportService';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

interface ImportError {
  row: number;
  field: string;
  message: string;
  value: any;
}

interface ImportResult {
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: ImportError[];
  importedData: PackPrice[];
}

interface PackPrice {
  packId: string;
  packName: string;
  areaCode: string;
  areaName: string;
  oldPrice: number;
  newPrice: number;
  currency: string;
  status: 'success' | 'error' | 'warning';
}

const ImportPacksPrices: React.FC = () => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState<PackPrice[]>([]);

  // Template Excel que l'utilisateur peut télécharger
  const downloadTemplate = useCallback(async () => {
    try {
      // En production, utiliser le vrai service
      // const blob = await packPriceImportService.downloadTemplate();
      
      // Pour l'instant, créer un template simple
      const templateData = [
        ['Pack ID', 'Nom du Pack', 'Code Zone', 'Nom Zone', 'Ancien Prix', 'Nouveau Prix', 'Devise'],
        ['PACK001', 'Pack France 5GB', 'FR', 'France', '15.00', '12.00', 'EUR'],
        ['PACK002', 'Pack Europe 10GB', 'EU', 'Europe', '25.00', '22.00', 'EUR'],
        ['PACK003', 'Pack Maroc 3GB', 'MA', 'Maroc', '10.00', '8.00', 'EUR']
      ];

      // Convertir en CSV pour simplifier (en production, utiliser une vraie librairie Excel)
      const csvContent = templateData.map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'template_prix_packs.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      message.success(t('admin.importPacksPrices.templateDownloaded'));
    } catch (error) {
      message.error(t('admin.importPacksPrices.templateDownloadError'));
      console.error(error);
    }
  }, [t]);

  // Analyse du fichier Excel
  const parseExcelFile = useCallback(async (file: File): Promise<ImportResult> => {
    try {
      // Créer un FormData pour envoyer le fichier
      const formData = new FormData();
      formData.append('file', file);

      // Envoyer le fichier au serveur pour analyse
      const response = await apiClient.post('/admin/import-prices-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Retourner le résultat du serveur
      return response.data;
    } catch (error: any) {
      console.error('Erreur lors de l\'analyse du fichier:', error);
      
      // En cas d'erreur, retourner un résultat par défaut ou relancer l'erreur
      if (error.response?.data) {
        throw new Error(error.response.data.message || 'Erreur lors de l\'analyse du fichier');
      }
      
      throw new Error('Erreur de connexion au serveur');
    }
  }, []);

  // Propriétés de l'upload
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    accept: '.xlsx,.xls,.csv',
    beforeUpload: (file) => {
      const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                    file.type === 'application/vnd.ms-excel' ||
                    file.type === 'text/csv';
      
      if (!isExcel) {
        message.error(t('admin.importPacksPrices.fileTypeError'));
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error(t('admin.importPacksPrices.fileSizeError'));
        return false;
      }

      return false; // Empêcher l'upload automatique
    },
    onChange: (info) => {
      setFileList(info.fileList.slice(-1)); // Garder seulement le dernier fichier
    },
    onDrop: (e) => {
      console.log('Fichiers déposés', e.dataTransfer.files);
    },
  };

  // Traitement de l'import
  const handleImport = useCallback(async () => {
    if (fileList.length === 0) {
      message.error(t('admin.importPacksPrices.noFileSelected'));
      return;
    }

    const file = fileList[0].originFileObj;
    if (!file) return;

    try {
      setUploading(true);
      message.loading(t('admin.importPacksPrices.analyzing'), 0);

      const result = await parseExcelFile(file);
      setImportResult(result);
      setPreviewData(result.importedData);
      //setShowPreview(true);

      message.destroy();
      message.success(t('admin.importPacksPrices.analyzeSuccess'));
    } catch (error) {
      message.destroy();
      message.error(t('admin.importPacksPrices.analyzeError'));
      console.error(error);
    } finally {
      setUploading(false);
    }
  }, [fileList, parseExcelFile]);

  // Confirmation de l'import
  const confirmImport = useCallback(async () => {
    if (!importResult) return;

    Modal.confirm({
      title: t('admin.importPacksPrices.confirmTitle'),
      content: t('admin.importPacksPrices.confirmContent', { count: importResult.successCount }),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          setUploading(true);
          
          // Envoyer les données confirmées au serveur
          const dataToImport = importResult.importedData
            .filter(item => item.status === 'success' || item.status === 'warning')
            .map(item => ({
              packId: item.packId,
              packName: item.packName,
              areaCode: item.areaCode,
              areaName: item.areaName,
              oldPrice: item.oldPrice,
              newPrice: item.newPrice,
              currency: item.currency
            }));

          const response = await apiClient.post('/api/admin/import-prices-excel', {
            prices: dataToImport
          });
          
          message.success(`${response.data.importedCount || importResult.successCount} prix importés avec succès`);
          
          // Reset du formulaire
          setFileList([]);
          setImportResult(null);
          setShowPreview(false);
          setPreviewData([]);
        } catch (error: any) {
          const errorMessage = error.response?.data?.message || 'Erreur lors de l\'import';
          message.error(errorMessage);
          console.error(error);
        } finally {
          setUploading(false);
        }
      }
    });
  }, [importResult]);

  // Colonnes pour la table de prévisualisation
  const previewColumns = [
    {
      title: 'Pack ID',
      dataIndex: 'packId',
      key: 'packId',
    },
    {
      title: () => t('admin.importPacksPrices.packName'),
      dataIndex: 'packName',
      key: 'packName',
    },
    {
      title: () => t('admin.importPacksPrices.area'),
      dataIndex: 'areaName',
      key: 'areaName',
      render: (text: string, record: PackPrice) => `${text} (${record.areaCode})`
    },
    {
      title: () => t('admin.importPacksPrices.oldPrice'),
      dataIndex: 'oldPrice',
      key: 'oldPrice',
      render: (price: number, record: PackPrice) => `${price.toFixed(2)} ${record.currency}`
    },
    {
      title: () => t('admin.importPacksPrices.newPrice'),
      dataIndex: 'newPrice',
      key: 'newPrice',
      render: (price: number, record: PackPrice) => `${price.toFixed(2)} ${record.currency}`
    },
    {
      title: () => t('admin.importPacksPrices.difference'),
      key: 'difference',
      render: (_, record: PackPrice) => {
        const diff = record.newPrice - record.oldPrice;
        const isIncrease = diff > 0;
        return (
          <Text type={isIncrease ? 'danger' : 'success'}>
            {isIncrease ? '+' : ''}{diff.toFixed(2)} {record.currency}
          </Text>
        );
      }
    },
    {
      title: () => t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          success: { color: 'green', icon: <CheckCircleOutlined />, text: t('common.valid') },
          warning: { color: 'orange', icon: <ExclamationCircleOutlined />, text: t('common.warning', 'Attention') },
          error: { color: 'red', icon: <CloseCircleOutlined />, text: t('common.error') }
        };
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      }
    }
  ];

  // Colonnes pour la table des erreurs
  const errorColumns = [
    {
      title: () => t('admin.importPacksPrices.errorRow'),
      dataIndex: 'row',
      key: 'row',
    },
    {
      title: () => t('admin.importPacksPrices.errorField'),
      dataIndex: 'field',
      key: 'field',
    },
    {
      title: () => t('admin.importPacksPrices.errorMessage'),
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: () => t('admin.importPacksPrices.errorValue'),
      dataIndex: 'value',
      key: 'value',
      render: (value: any) => <Text code>{String(value)}</Text>
    }
  ];

  return (
    <div>
      <Card>
        <Title level={2}>
          <FileExcelOutlined /> {t('admin.importPacksPrices.pageTitle')}
        </Title>
        
        <Alert
          message="Information"
          description={t('admin.importPacksPrices.alertDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Row gutter={24}>
          <Col span={12}>
            <Card title={t('admin.importPacksPrices.step1Title')} size="small">
              <Paragraph>
                {t('admin.importPacksPrices.step1Description')}
              </Paragraph>
              <Button 
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
                type="default"
              >
                {t('admin.importPacksPrices.downloadTemplateBtn')}
              </Button>
            </Card>
          </Col>
          
          <Col span={12}>
            <Card title={t('admin.importPacksPrices.step2Title')} size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Dragger {...uploadProps} style={{ marginBottom: 16 }}>
                  <p className="ant-upload-drag-icon">
                    <FileExcelOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                  </p>
                  <p className="ant-upload-text">
                    {t('admin.importPacksPrices.dragText')}
                  </p>
                  <p className="ant-upload-hint">
                    {t('admin.importPacksPrices.formatHint')}
                  </p>
                </Dragger>

                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={handleImport}
                  loading={uploading}
                  disabled={fileList.length === 0}
                  size="large"
                  block
                >
                  {t('admin.importPacksPrices.analyzeBtn')}
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>

        {uploading && (
          <Card style={{ marginTop: 24 }}>
            <Progress percent={50} status="active" />
            <Text>{t('admin.importPacksPrices.analyzingProgress')}</Text>
          </Card>
        )}

        {/*importResult && (
          <Card title="Résultats de l'analyse" style={{ marginTop: 24 }}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col span={6}>
                <Statistic
                  title="Total des lignes"
                  value={importResult.totalRows}
                  prefix={<InfoCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Lignes valides"
                  value={importResult.successCount}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Erreurs"
                  value={importResult.errorCount}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="Taux de réussite"
                  value={((importResult.successCount / importResult.totalRows) * 100).toFixed(1)}
                  suffix="%"
                  valueStyle={{ 
                    color: importResult.errorCount === 0 ? '#3f8600' : '#faad14' 
                  }}
                />
              </Col>
            </Row>

            {importResult.errorCount > 0 && (
              <>
                <Divider />
                <Title level={4}>Erreurs détectées</Title>
                <Table
                  columns={errorColumns}
                  dataSource={importResult.errors}
                  pagination={false}
                  size="small"
                  rowKey={(record, index) => `error-${index}`}
                />
              </>
            )}

            {importResult.successCount > 0 && (
              <div style={{ marginTop: 16 }}>
                <Space>
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={confirmImport}
                    disabled={importResult.successCount === 0}
                  >
                    Confirmer l'import ({importResult.successCount} prix)
                  </Button>
                  
                  <Button onClick={() => setShowPreview(true)}>
                    Prévisualiser les données
                  </Button>
                </Space>
              </div>
            )}
          </Card>
        )*/}

        {/* Modal de prévisualisation */}
        <Modal
          title={t('admin.importPacksPrices.previewModalTitle')}
          visible={showPreview}
          onCancel={() => setShowPreview(false)}
          footer={[
            <Button key="cancel" onClick={() => setShowPreview(false)}>
              {t('common.close')}
            </Button>,
            <Button
              key="import"
              type="primary"
              onClick={() => {
                setShowPreview(false);
                confirmImport();
              }}
              disabled={!importResult || importResult.successCount === 0}
            >
              {t('admin.importPacksPrices.proceedBtn')}
            </Button>
          ]}
          width={1200}
        >
          <Table
            columns={previewColumns}
            dataSource={previewData}
            pagination={{ pageSize: 10 }}
            rowKey="packId"
            scroll={{ x: 'max-content' }}
          />
        </Modal>
      </Card>
    </div>
  );
};

export default ImportPacksPrices;
